import { useEffect, useRef } from "react";
import { useMedia } from "react-use";

export default function DigitalConsciousnessIcon() {
  const canvasRef = useRef(null);
  const isMobileSize = useMedia("(max-width: 480px)");

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const size = isMobileSize ? 310 : 320;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.scale(dpr, dpr);

    const centerX = size / 2;
    const centerY = size / 2;
    const coreRadius = 60;

    // Data stream particles
    const streams = [];

    // Create data streams
    for (let i = 0; i < 50; i++) {
      streams.push({
        angle: Math.random() * Math.PI * 2,
        distance: 80 + Math.random() * 100,
        speed: 0.5 + Math.random() * 1.5,
        size: 0.5 + Math.random() * 1,
        opacity: 0.05 + Math.random() * 0.1,
        orbitSpeed: 0.001 + Math.random() * 0.002,
        verticalOffset: -40 + Math.random() * 80,
      });
    }

    let animationFrame;
    let time = 0;

    function animate() {
      if (!ctx || !canvas) return;

      ctx.clearRect(0, 0, size, size);
      time += 0.01;

      // Pulse effect
      const pulse = Math.sin(time * 2) * 0.15 + 1;

      // Draw outer glow layers
      for (let i = 8; i > 0; i--) {
        const glowRadius = coreRadius * pulse + i * 15;
        const gradient = ctx.createRadialGradient(
          centerX,
          centerY,
          coreRadius * pulse,
          centerX,
          centerY,
          glowRadius,
        );
        gradient.addColorStop(0, `rgba(253, 91, 56, ${0.4 / i})`);
        gradient.addColorStop(1, "rgba(21, 93, 252, 0)");
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(centerX, centerY, glowRadius, 0, Math.PI * 2);
        ctx.fill();
      }

      // Draw data streams
      streams.forEach((stream, index) => {
        stream.angle += stream.orbitSpeed;

        const x = centerX + Math.cos(stream.angle) * stream.distance;
        const y =
          centerY +
          Math.sin(stream.angle) * stream.distance * 0.5 +
          stream.verticalOffset * Math.sin(time + index);

        const distanceFromCenter = Math.sqrt(
          Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2),
        );
        const fadeDistance = 180;
        const fadeFactor = Math.max(
          0,
          (fadeDistance - Math.abs(distanceFromCenter - 120)) / fadeDistance,
        );

        // Draw stream particle
        ctx.fillStyle = `rgba(30, 30, 30, ${stream.opacity * fadeFactor})`;
        ctx.beginPath();
        ctx.arc(x, y, stream.size, 0, Math.PI * 2);
        ctx.fill();

        // Draw connecting lines occasionally
        if (index % 8 === 0) {
          const nextStream = streams[(index + 1) % streams.length];
          const nextX =
            centerX + Math.cos(nextStream.angle) * nextStream.distance;
          const nextY =
            centerY +
            Math.sin(nextStream.angle) * nextStream.distance * 0.5 +
            nextStream.verticalOffset * Math.sin(time + index + 1);

          ctx.strokeStyle = `rgba(30, 30, 30, ${0.03 * fadeFactor})`;
          ctx.lineWidth = 0.3;
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(nextX, nextY);
          ctx.stroke();
        }
      });

      // Draw core sphere with glassmorphism
      const coreGradient = ctx.createRadialGradient(
        centerX - coreRadius * 0.3,
        centerY - coreRadius * 0.3,
        0,
        centerX,
        centerY,
        coreRadius * pulse,
      );
      coreGradient.addColorStop(0, "rgba(255, 255, 255, 0.9)");
      coreGradient.addColorStop(0.3, "rgba(253, 91, 56, 0.8)");
      coreGradient.addColorStop(1, "rgba(253, 91, 56, 1)");

      ctx.fillStyle = coreGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, coreRadius * pulse, 0, Math.PI * 2);
      ctx.fill();

      // Glass highlight
      const highlightGradient = ctx.createRadialGradient(
        centerX - coreRadius * 0.4,
        centerY - coreRadius * 0.4,
        0,
        centerX - coreRadius * 0.4,
        centerY - coreRadius * 0.4,
        coreRadius * 0.6,
      );
      highlightGradient.addColorStop(0, "rgba(255, 255, 255, 0.8)");
      highlightGradient.addColorStop(1, "rgba(255, 255, 255, 0)");

      ctx.fillStyle = highlightGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, coreRadius * pulse, 0, Math.PI * 2);
      ctx.fill();

      // Inner glow
      const innerGlow = ctx.createRadialGradient(
        centerX,
        centerY,
        0,
        centerX,
        centerY,
        coreRadius * pulse * 0.8,
      );
      innerGlow.addColorStop(0, "rgba(255, 255, 255, 0.6)");
      innerGlow.addColorStop(0.5, "rgba(253, 91, 56, 0.3)");
      innerGlow.addColorStop(1, "rgba(21, 93, 252, 0)");

      ctx.fillStyle = innerGlow;
      ctx.beginPath();
      ctx.arc(centerX, centerY, coreRadius * pulse, 0, Math.PI * 2);
      ctx.fill();

      animationFrame = requestAnimationFrame(animate);
    }

    animate();

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [isMobileSize, canvasRef]);

  return (
    <div className="relative w-[310px] h-[310px] md:w-[320px] md:h-[320px] flex items-center justify-center">
      <canvas ref={canvasRef} className="absolute inset-0" />
    </div>
  );
}
